import { Entity, mapEntity } from '@toggle/toggle';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { formatPriceDisplay } from '~/components/price-display-view/helpers/price-display-helpers';
import { mapEntityToPriceEntity, useLivePrice } from '~/hooks/use-live-price';
import { usePreliminaryPrice } from '~/react-query-hooks/prices/usePreliminaryPrice';
import { useSnakeMeta } from '~/react-query-hooks/snakes/useSnake';
import { getPriceData } from '~/widgets/asset-overview-widget/utils/asset-box/asset-box-helpers';

import * as S from './company-card/CompanyCard.styles';
import { isReferenceDateOlderThanTwoDays } from './utils';

export interface PriceChangeProps {
  companyData: Entity;
  showPostfix?: boolean;
  referenceDate?: string;
}

export const PriceChange = ({
  companyData,
  showPostfix = true,
  referenceDate,
}: PriceChangeProps) => {
  const { t } = useTranslation(['earnings']);

  const mappedEntity = mapEntity(companyData);
  const { data: snakeMeta } = useSnakeMeta(companyData?.default_snake);
  const { data: preliminaryPrice } = usePreliminaryPrice(
    companyData.subscribable_ticker ?? ''
  );

  const priceEntities = mapEntityToPriceEntity(companyData, snakeMeta);
  const shouldDisableLive = isReferenceDateOlderThanTwoDays(referenceDate);

  const livePriceData = useLivePrice(shouldDisableLive ? [] : priceEntities);
  const livePriceInfo = getPriceData({
    hoverData: undefined,
    livePriceData,
    entity: mappedEntity,
    ts: [],
    priceEntity: priceEntities[0],
  });
  const price = formatPriceDisplay({
    exchange: companyData.exchange.code,
    livePriceInfo,
    preliminaryPrice: preliminaryPrice?.data,
    meta: snakeMeta,
    isHovered: false,
    t,
  });

  if (!livePriceInfo || !price.primary.priceChange) {
    return null;
  }

  return (
    <S.PriceChangeWrapper>
      <S.PriceChangeContainer $status={price.primary.priceChange.status}>
        {price.primary.priceChange.proportionChange}
      </S.PriceChangeContainer>
      {showPostfix && (
        <S.DateLabel>
          {t(
            price.primary.isLive
              ? `earningsInsight.drawer.earningsRecap.liveData`
              : `earningsInsight.drawer.earningsRecap.asOfLastClose`
          )}
        </S.DateLabel>
      )}
    </S.PriceChangeWrapper>
  );
};
